import {
  resolvePublicSession,
  submitPublicIntake as submitPublicIntakeApi,
  getPublicSlots,
  selectPublicSlot,
  createPublicSlotHold as createPublicSlotHoldApi,
  releasePublicSlotHold as releasePublicSlotHoldApi,
  createPublicOrder as createPublicOrderApi,
  verifyPublicPayment as verifyPublicPaymentApi,
  updatePublicPaymentStatus as updatePublicPaymentStatusApi,
} from "./consultationApiClient";

export type ConsultationRouteMode = "token" | "phone" | "new_checkout";

export type ConsultationStep =
  | "identify"
  | "intake"
  | "slot"
  | "payment"
  | "confirmed"
  | "scheduled"
  | "manual_review"
  | "scheduling_pending"
  | "resume_payment"
  | "not_found"
  | "error"
  | "loading";

export interface ConsultationProfile {
  key: string;
  name_for_consult: string;
  client_phone: string;
  client_email: string;
  beneficiary_relation: string;
  dob: string;
  tob: string;
  pob: string;
  birth_details_uncertain: boolean;
  preferred_date: string;
  preferred_time: string;
  timezone: string;
  problem: string;
  concern_summary?: string;
  consultation_count: number;
  latest_consultation_at: string | null;
}

export interface ConsultationSummary {
  id: number;
  status: string;
  consultation_type: string;
  name_for_consult: string;
  client_phone: string;
  client_email: string;
  beneficiary_relation: string;
  dob: string;
  tob: string;
  pob: string;
  birth_details_uncertain: boolean;
  preferred_date: string;
  preferred_time: string;
  timezone: string;
  concern_area?: string;
  problem: string;
  details_submitted: boolean;
  scheduled_at: string | null;
  source_note: string;
  missing_fields: string[];
  client_input?: any;
  is_paid?: boolean;
  state?: string;
  missing_profile_fields?: string[];
  slot_pending?: boolean;
  requires_client_action?: boolean;
  slot_duration?: number | null;
  payment_amount?: number | null;
  source_payment_order_id?: string | null;
}

export interface ConsultationSessionPayload {
  token_type?: string;
  token: string;
  request_id: string | null;
  status: string;
  mode: string;
  expires_at: string | null;
  completed_at: string | null;
  last_message_sent_at?: string | null;
  consultations: ConsultationSummary[];
  existing_profiles: ConsultationProfile[];
}

export interface ResolvedConsultationSession {
  success: boolean;
  lookup_mode?: "token" | "phone";
  flow: "existing_consultation" | "resume_payment" | "new_checkout";
  next_step: string;
  session_token?: string;
  session?: ConsultationSessionPayload;
  consultation?: ConsultationSummary;
  consultations?: ConsultationSummary[];
  existing_profiles?: ConsultationProfile[];
}

export interface ConsultationIntakeDraft {
  name_for_consult: string;
  client_phone: string;
  client_email: string;
  beneficiary_relation: string;
  dob: string;
  tob: string;
  pob: string;
  birth_details_uncertain: boolean;
  preferred_date: string;
  preferred_time: string;
  timezone: string;
  problem: string;
  concern_area?: string;
}

export interface ConsultationSlotOption {
  date: string;
  label: string;
  preferred_date: string;
  preferred_time: string;
  start_iso: string;
  available_expert_count: number;
}

export interface ConsultationSlotDay {
  date: string;
  weekday: number;
  blocked: boolean;
  slots: ConsultationSlotOption[];
}

export interface ConsultationSlotInventory {
  success: boolean;
  consultation_id: number | null;
  timezone: string;
  duration_minutes: number;
  buffer_time: number;
  occupied_minutes: number;
  days: ConsultationSlotDay[];
}

export interface ConsultationSlotSelectionResult {
  success: boolean;
  consultation_id: number;
  consultation: ConsultationSummary;
  remaining_missing_fields: string[];
  next_step: string;
  auto_schedule_result?: unknown;
  auto_schedule_error?: string | null;
  slot_inventory?: ConsultationSlotInventory;
}

export interface ConsultationSlotHoldResult {
  success: boolean;
  consultationId: number;
  holdId: number;
  expiresAt: string;
  hold: {
    id: number;
    consultation_id: number;
    expert_id: number;
    preferred_date: string;
    preferred_time: string;
    start_at: string;
    end_at: string;
    duration_minutes: number;
    buffer_time: number;
    status: string;
    expires_at: string;
  };
  pricing: ConsultationPricingSummary & { bufferTime?: number };
  selectedSlot: {
    preferred_date: string;
    preferred_time: string;
    start_iso: string;
    end_iso: string;
  };
}

export interface ConsultationIntakeSubmissionResult {
  success: boolean;
  consultation_ids: number[];
  request_status: { completed: boolean; status: string } | null;
  auto_schedule_results: Array<Record<string, unknown>>;
}

export interface ConsultationPricingSummary {
  id: string;
  durationMinutes: number;
  amount: number;
  currency: string;
  label: string | null;
  description: string | null;
}

export interface ConsultationOrderResult {
  success: boolean;
  consultationId: number;
  pricing: ConsultationPricingSummary;
  razorpay: {
    key: string;
    orderId: string;
    amount: number;
    currency: string;
  };
}

export interface ConsultationPaymentVerificationResult {
  success: boolean;
  consultationId: number;
  alreadyPaid?: boolean;
  status?: string;
  next_step?: string;
  missing_fields?: string[];
  error?: string;
}

export function normalizePhone(value: string) {
  return value.replace(/\D/g, "").slice(-10);
}

export function normalizeTokenFromSearch(
  token: string | null,
  legacyId: string | null,
) {
  return String(token || legacyId || "").trim();
}

export async function resolveConsultationSession(input: {
  token?: string;
  phone?: string;
  consultationId?: number;
}): Promise<ResolvedConsultationSession> {
  return resolvePublicSession({
    ...(input.token ? { token: input.token } : {}),
    ...(input.phone ? { phone: normalizePhone(input.phone) } : {}),
    ...(input.consultationId ? { consultationId: input.consultationId } : {}),
  });
}

export async function submitConsultationIntake(input: {
  token: string;
  apply_mode: "shared" | "per_consultation";
  shared_details?: ConsultationIntakeDraft;
  consultation_details?: Array<{
    consultation_id: number;
    details: ConsultationIntakeDraft;
  }>;
}): Promise<ConsultationIntakeSubmissionResult> {
  return submitPublicIntakeApi(input);
}

export async function listConsultationSlots(input: {
  token?: string;
  consultationId?: number;
  durationMinutes?: number;
  fromDate?: string;
  days?: number;
}): Promise<ConsultationSlotInventory> {
  return getPublicSlots(input);
}

export async function selectConsultationSlot(input: {
  token: string;
  consultationId?: number;
  preferred_date: string;
  preferred_time: string;
  timezone: string;
}): Promise<ConsultationSlotSelectionResult> {
  return selectPublicSlot(input);
}

export async function createConsultationSlotHold(input: {
  consultationId?: number;
  name: string;
  contact: string;
  email?: string;
  concern?: string;
  concernDetail?: string;
  concernArea?: string;
  concernSummary?: string;
  dob?: string;
  tob?: string;
  pob?: string;
  beneficiaryRelation?: string;
  preferred_date: string;
  preferred_time: string;
  durationMinutes: number;
  pricingId?: string;
  pricing_id?: string;
  marketingPricingOnly?: boolean;
  marketingActive?: boolean;
  sourceNote?: string;
  origin?: string;
  timezone?: string;
}): Promise<ConsultationSlotHoldResult> {
  return createPublicSlotHoldApi(input as Record<string, unknown>);
}

export async function releaseConsultationSlotHold(input: {
  holdId?: number;
  consultationId?: number;
}): Promise<{ success: boolean; holdId?: number | null; consultationId?: number | null }> {
  return releasePublicSlotHoldApi(input);
}

export async function createConsultationOrder(input: {
  token?: string;
  consultation: {
    consultationId?: number;
    holdId?: number;
    hold_id?: number;
    name: string;
    contact: string;
    email: string;
    type?: string;
    promise?: string;
    concern?: string;
    concernDetail?: string;
    concernArea?: string;
    concernSummary?: string;
    dob?: string;
    tob?: string;
    pob?: string;
    gender?: string;
    unsureBirthDetails?: boolean;
    birthDetailsUncertain?: boolean;
    beneficiaryRelation?: string;
    preferredDate?: string;
    preferredTime?: string;
    durationMinutes: number;
    pricingId?: string;
    pricing_id?: string;
    marketingPricingOnly?: boolean;
    marketingActive?: boolean;
    source?: string;
    sourceLabel?: string;
    origin?: string;
    attribution?: Record<string, string>;
    timezone?: string;
    campaignCode?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    landingPage?: string;
    landingVariantId?: string;
    assignmentId?: string;
    sourceNote?: string;
    fbclid?: string;
    fbp?: string;
    fbc?: string;
    gclid?: string;
    ttclid?: string;
    campaignId?: string;
    campaignName?: string;
    adsetId?: string;
    adsetName?: string;
    adId?: string;
    adName?: string;
    creativeId?: string;
    creativeName?: string;
    placement?: string;
    siteSourceName?: string;
    platformPosition?: string;
    videoId?: string;
    videoName?: string;
  };
}): Promise<ConsultationOrderResult> {
  return createPublicOrderApi(input as Record<string, unknown>);
}

export async function verifyConsultationPayment(input: {
  consultationId: number;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}): Promise<ConsultationPaymentVerificationResult> {
  return verifyPublicPaymentApi(input);
}

export async function updateConsultationPaymentStatus(input: {
  consultationId: number;
  status: "payment_abandoned" | "payment_failed" | "pending_payment";
}): Promise<ConsultationPaymentVerificationResult> {
  return updatePublicPaymentStatusApi(input);
}

export async function ensureConsultationIntakeLink(input: {
  authToken: string;
  consultationId: number;
}): Promise<{
  success: boolean;
  token?: string;
  link?: string;
  error?: string;
}> {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://astroji.com";
  return {
    success: true,
    token: String(input.consultationId),
    link: `${origin}/consultations/intake?token=${input.consultationId}`,
  };
}

